import { CircuitDesign } from '../utils/circuitBuilder';
import { SceneryItem } from '../types/track';

type Point = readonly [number, number];

function circuit(
  id: string,
  name: string,
  description: string,
  points: Point[],
  landmarks: [string, number, number][],
  counterclockwise = false,
  scale = 1200
): CircuitDesign {
  let route = points;
  const area = points.reduce((sum, [x, y], i) => {
    const next = points[(i + 1) % points.length]!;
    return sum + x * next[1] - next[0] * y;
  }, 0);
  if (area > 0 === counterclockwise) route = [points[0]!, ...points.slice(1).reverse()];
  const nodes = route.map(([x, y], i) => {
    const before = route[(i + route.length - 1) % route.length]!;
    const after = route[(i + 1) % route.length]!;
    const incoming = Math.hypot(x - before[0], y - before[1]);
    const outgoing = Math.hypot(after[0] - x, after[1] - y);
    const dx = (x - before[0]) / incoming + (after[0] - x) / outgoing;
    const dy = (y - before[1]) / incoming + (after[1] - y) / outgoing;
    if (Math.hypot(dx, dy) < 1e-8) throw new Error(`Reverse tangent in ${id} at node ${i}`);
    return {
      x: x * scale,
      y: y * scale,
      heading: (Math.atan2(dy, dx) * 180) / Math.PI,
      handle: Math.max(incoming, outgoing) * scale * 0.48,
    };
  });
  const scenery: SceneryItem[] = landmarks.map(([label, x, y], i) => ({
    id: `${id}-label-${i}`,
    type: 'label',
    label,
    position: { x: x * scale, y: y * scale },
    scale: i === 0 ? 2.6 : 1.3,
    rotation: 0,
  }));
  const start = nodes[0]!;
  const heading = (start.heading * Math.PI) / 180;
  for (const [i, side] of [-1, 1].entries()) {
    scenery.push({
      id: `${id}-paddock-${i}`,
      type: i === 0 ? 'building' : 'grandstand',
      label: i === 0 ? 'PIT BUILDING' : 'MAIN GRANDSTAND',
      position: {
        x: start.x - Math.sin(heading) * 650 * side,
        y: start.y + Math.cos(heading) * 650 * side,
      },
      scale: 3,
      rotation: heading,
    });
  }
  return {
    id,
    name,
    category: 'Formula 1',
    description,
    width: Math.ceil(Math.max(...points.map(p => p[0])) + 1.5) * scale,
    height: Math.ceil(Math.max(...points.map(p => p[1])) + 1.5) * scale,
    roadWidth: 240,
    difficulty: 'hard',
    nodes,
    scenery,
  };
}

export const F1_WESTERN_DESIGNS: CircuitDesign[] = [
  circuit(
    'monaco-grand-prix',
    'Monaco Grand Prix',
    'Casino climb, a generous Grand Hotel hairpin, tunnel sweep and harbor-side swimming-pool esses.',
    [
      [6, 10],
      [3, 10],
      [1.5, 8.8],
      [1.5, 6.8],
      [3.5, 5.2],
      [2.7, 3.6],
      [3.5, 1.6],
      [5.5, 1.4],
      [7.2, 2.2],
      [7.8, 3.8],
      [6.5, 4.7],
      [5.4, 4],
      [4.8, 5.7],
      [6.2, 6.8],
      [8.8, 5.8],
      [10.7, 6.7],
      [10.8, 8.5],
      [9, 10],
    ],
    [
      ['MONACO', 8.3, 8.2],
      ['SAINTE DEVOTE', 1.1, 10.2],
      ['CASINO', 4.6, 1],
      ['GRAND HOTEL', 8.8, 3.8],
      ['TUNNEL', 7.6, 5],
      ['SWIMMING POOL', 11.3, 9.4],
    ]
  ),
  circuit(
    'barcelona-grand-prix',
    'Barcelona-Catalunya Grand Prix',
    'A long pit straight, opening esses, sweeping Turn 3, La Caixa and the restored fast final bends.',
    [
      [6, 10],
      [2.5, 10],
      [1.5, 8.4],
      [2.5, 6.8],
      [1.6, 5.5],
      [2, 3],
      [4, 1.5],
      [6.5, 1.8],
      [8.5, 3.2],
      [8.5, 5],
      [6.8, 5.7],
      [5.2, 4.8],
      [4.4, 6.1],
      [5.5, 7.4],
      [8, 7],
      [10.5, 7.8],
      [10.2, 9.5],
      [8.5, 10],
    ],
    [
      ['BARCELONA-CATALUNYA', 6, 8.8],
      ['ELF / TURN 3', 1.1, 4.2],
      ['CAMPSA', 9.5, 4.2],
      ['LA CAIXA', 4.9, 4],
      ['FAST FINAL SECTOR', 11.4, 8.5],
    ]
  ),
  circuit(
    'red-bull-ring-grand-prix',
    'Red Bull Ring Grand Prix',
    'Three long power sections, the Remus hairpin and fast downhill-style double right finish.',
    [
      [6, 10],
      [2, 10],
      [1.5, 8],
      [3, 6.5],
      [4.5, 3],
      [5.5, 1.5],
      [7.2, 1.3],
      [9, 2.2],
      [10, 4.5],
      [8.5, 6],
      [6.5, 6.5],
      [6.5, 8],
      [8.7, 8],
      [10.5, 9],
      [9, 10],
    ],
    [
      ['RED BULL RING', 4, 8.5],
      ['NIKI LAUDA', 1.3, 10.8],
      ['REMUS', 6.5, 0.8],
      ['SCHLOSSGOLD', 10.8, 3.4],
      ['RINDT / RED BULL', 10.8, 10.6],
    ]
  ),
  circuit(
    'hungaroring-grand-prix',
    'Hungaroring Grand Prix',
    'A long opening straight gives way to a compact sequence of flowing bends, chicane and final hairpin.',
    [
      [6, 10],
      [2, 10],
      [1.3, 8],
      [2.7, 6.6],
      [1.6, 4.5],
      [2.4, 2.4],
      [4.5, 1.3],
      [6.5, 2],
      [7.7, 3.5],
      [9.5, 3],
      [10.6, 4.6],
      [9, 5.8],
      [7.6, 5.1],
      [6, 6.2],
      [6.5, 7.7],
      [9, 7.8],
      [10, 9.5],
      [8.5, 10],
    ],
    [
      ['HUNGARORING', 4.2, 8.3],
      ['OPENING HAIRPIN', 0.9, 9.4],
      ['CHICANE', 8.5, 2],
      ['TECHNICAL INFIELD', 4.2, 4.8],
      ['FINAL HAIRPIN', 10.8, 9.8],
    ]
  ),
  circuit(
    'zandvoort-grand-prix',
    'Zandvoort Grand Prix',
    'Tarzan and Hugenholtz loops, dune-like esses, Scheivlak and a broad final Arie Luyendyk sweep; flat physics.',
    [
      [6, 10],
      [2.5, 10],
      [1.2, 8.8],
      [1.8, 7],
      [3.4, 6.3],
      [2.7, 4.7],
      [1.5, 3.5],
      [2.2, 1.7],
      [4.5, 1.2],
      [6, 2.8],
      [7.8, 2],
      [9.8, 3.3],
      [9, 5],
      [7.4, 5.7],
      [8.7, 7.2],
      [10.6, 8],
      [10.2, 9.5],
      [8.5, 10],
    ],
    [
      ['ZANDVOORT', 5.5, 7.8],
      ['TARZAN', 1.1, 10.1],
      ['HUGENHOLTZ', 0.9, 4.7],
      ['SCHEIVLAK', 7.6, 1.2],
      ['ARIE LUYENDYK', 11.3, 8.6],
    ]
  ),
  circuit(
    'madrid-grand-prix',
    'Madrid Madring Grand Prix',
    'IFEMA-style street straights, a technical exhibition sector and a sweeping La Monumental arc; no simulated banking.',
    [
      [6, 10],
      [2, 10],
      [1.3, 8],
      [2.5, 6.5],
      [1.5, 5],
      [2.5, 3.5],
      [2, 1.5],
      [4.5, 1.3],
      [6, 2.5],
      [7.5, 1.8],
      [10, 2.3],
      [11.5, 4.5],
      [11, 6.8],
      [9, 7.8],
      [7, 6.8],
      [5.4, 7.4],
      [6.5, 8.7],
      [9.5, 9],
      [9, 10],
    ],
    [
      ['MADRING', 7.5, 4.6],
      ['IFEMA', 1, 7],
      ['EXHIBITION ESSES', 4, 2.2],
      ['LA MONUMENTAL', 12.1, 5],
      ['VALDEBEBAS', 7.6, 8.2],
    ]
  ),
  circuit(
    'baku-grand-prix',
    'Baku City Grand Prix',
    'A very long waterfront straight, ninety-degree city blocks and a widened old-town castle sequence.',
    [
      [6, 11],
      [2, 11],
      [1.5, 9],
      [1.5, 6.5],
      [3, 6.5],
      [3, 4.5],
      [1.5, 4.5],
      [1.5, 2],
      [3.5, 1.2],
      [5.5, 2],
      [5.5, 3.5],
      [7.2, 4.2],
      [7.2, 6.3],
      [9, 6.3],
      [10.5, 8.5],
      [10.5, 11],
      [8.5, 11],
    ],
    [
      ['BAKU', 6, 8.5],
      ['WATERFRONT', 6, 11.6],
      ['OLD TOWN / CASTLE', 3.5, 2.5],
      ['CITY BLOCKS', 0.9, 6.5],
    ],
    true
  ),
  circuit(
    'miami-grand-prix',
    'Miami Grand Prix',
    'Stadium-side opening esses, a marina loop, long acceleration zones and a technical overpass-inspired chicane.',
    [
      [6, 10],
      [2.5, 10],
      [1.5, 8.2],
      [2.6, 6.5],
      [1.7, 5],
      [3, 3.5],
      [2.8, 1.5],
      [5, 1.2],
      [6.5, 2.7],
      [8.3, 2],
      [10.5, 3.5],
      [10.5, 5.5],
      [8.5, 6],
      [7.5, 4.5],
      [5.5, 4.7],
      [5, 6.5],
      [7, 7.7],
      [10.5, 8],
      [10, 9.6],
      [8.5, 10],
    ],
    [
      ['MIAMI', 4.2, 8.5],
      ['STADIUM', 3.5, 4.7],
      ['MARINA', 8.5, 3.8],
      ['TECHNICAL CHICANE', 5.1, 3.9],
      ['BACK STRAIGHT', 10.7, 7],
    ],
    true
  ),
  circuit(
    'montreal-grand-prix',
    'Circuit Gilles Villeneuve Grand Prix',
    'An island-shaped course with alternating chicanes, a broad Casino hairpin and a fast Wall of Champions finish.',
    [
      [6, 11],
      [2.5, 11],
      [1.3, 9.3],
      [2, 7.5],
      [1.5, 5.5],
      [2.5, 4.2],
      [2, 2.5],
      [3.5, 1.2],
      [5.2, 1.5],
      [6.3, 3],
      [5.8, 4.7],
      [7.3, 6],
      [7.5, 8],
      [9.5, 9],
      [10.8, 10.7],
      [9, 11],
    ],
    [
      ['CIRCUIT GILLES VILLENEUVE', 4.7, 8.5],
      ['SENNA S', 0.8, 9.4],
      ['ISLAND CHICANES', 0.9, 4.1],
      ['CASINO HAIRPIN', 4, 0.6],
      ['WALL OF CHAMPIONS', 10.5, 11.5],
    ]
  ),
  circuit(
    'austin-grand-prix',
    'Circuit of The Americas Grand Prix',
    'Turn 1 hairpin, Silverstone-like esses, a long back straight, stadium bends and the multi-apex Turn 16-18 sweep.',
    [
      [6, 11],
      [2, 11],
      [1.2, 9],
      [2.5, 7.2],
      [1.5, 5.7],
      [3, 4.3],
      [2.4, 2.7],
      [4, 1.2],
      [6, 2],
      [7.3, 1.4],
      [9.7, 2],
      [11.5, 3.5],
      [11.5, 5.5],
      [9.5, 6.3],
      [7.5, 5.4],
      [5.8, 6.5],
      [6.5, 8],
      [9, 8.4],
      [10.5, 9.7],
      [9, 11],
    ],
    [
      ['CIRCUIT OF THE AMERICAS', 4.6, 9.3],
      ['TURN 1', 0.8, 10.4],
      ['ESSES', 1.1, 3.8],
      ['BACK STRAIGHT', 10.1, 0.9],
      ['STADIUM', 7.3, 6.5],
      ['TURN 16-18', 10.6, 8.2],
    ],
    true
  ),
  circuit(
    'mexico-city-grand-prix',
    'Mexico City Grand Prix',
    'A huge pit straight, opening chicane, tightening hairpin, flowing esses and the Foro Sol stadium sector.',
    [
      [6, 10],
      [2, 10],
      [1.3, 8.5],
      [2.4, 7],
      [1.5, 5.5],
      [1.5, 2.5],
      [3.5, 1.2],
      [5.5, 1.5],
      [6.6, 3],
      [5.5, 4.3],
      [6.8, 5.4],
      [8.5, 4.5],
      [10, 5.7],
      [9, 7],
      [10.6, 8.3],
      [10, 9.7],
      [8.5, 10],
    ],
    [
      ['MEXICO CITY', 4.3, 8],
      ['OPENING CHICANE', 0.9, 6.9],
      ['HAIRPIN', 4.2, 0.6],
      ['ESSES', 7.5, 3.8],
      ['FORO SOL', 11.1, 7.5],
    ]
  ),
  circuit(
    'las-vegas-grand-prix',
    'Las Vegas Strip Grand Prix',
    'A long Strip straight, Sphere-side switchbacks and broad city-block turns with a fast final sweep.',
    [
      [6, 11],
      [2, 11],
      [1.3, 9.4],
      [2, 7.7],
      [1.5, 6],
      [1.5, 2],
      [3.5, 1.3],
      [5.5, 2],
      [5.8, 3.8],
      [7.4, 4.5],
      [8.3, 3.5],
      [10.7, 4.2],
      [11, 7],
      [11, 9.5],
      [9, 11],
    ],
    [
      ['LAS VEGAS', 6, 8.7],
      ['THE STRIP', 0.9, 4],
      ['SPHERE SECTOR', 6.8, 2.7],
      ['KOVAL STRAIGHT', 11.6, 7.7],
      ['FINAL SWEEP', 10.5, 11.3],
    ],
    true
  ),
];
